import Countly from 'countly-sdk-web';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import config from '../config/config';

const TEAMS_TAB_VERSION = '1.0.0';

type ReservedKeys =
  | 'start'
  | 'visit'
  | 'bounce'
  | 'end'
  | 'name'
  | 'domain'
  | 'view'
  | 'segment'
  | 'platform';

type Segments = Record<Exclude<string, ReservedKeys>, string>;

type EventObject = {
  segmentation?: Segments;
  count?: number;
  sum?: number;
  dur?: number;
};

const AnalyticsHelper: IAnalyticsHelper = {
  _countly: Countly,
  _initialized: false,

  initCountly: () => {
    // Initialise the queue
    AnalyticsHelper._countly.q = Countly.q || [];

    // Track the initial page view
    AnalyticsHelper.trackPageView();

    // Set Countly to track by default
    AnalyticsHelper.trackSessions();
    AnalyticsHelper.trackClicks();
    AnalyticsHelper.trackScrolls();
    AnalyticsHelper.trackErrors();

    // Initialise Countly
    AnalyticsHelper._countly.init({
      app_key: config.countlyAppKey,
      url: config.countlyUrl || '',
      app_version: TEAMS_TAB_VERSION,
      debug: process.env.NODE_ENV === 'development',
    });

    AnalyticsHelper._initialized = true;
  },

  trackErrors: (data?: Segments) => {
    AnalyticsHelper._countly.q.push(['track_errors', data]);
  },

  logError: (error: Error, data?: Segments) => {
    AnalyticsHelper._countly.q.push(['log_error', error, data]);
  },

  recordError: (error: Error, nonFatal: boolean, data?: Segments) => {
    AnalyticsHelper._countly.q.push(['record_error', error, nonFatal, data]);
  },

  addLog: (log: string) => {
    AnalyticsHelper._countly.q.push(['add_log', log]);
  },

  addEvent: (key: string, event?: EventObject) => {
    AnalyticsHelper._countly.q.push([
      'add_event',
      {
        key,
        ...event,
      },
    ]);
  },

  startTimedEvent: (key: string) => {
    AnalyticsHelper._countly.q.push(['start_event', key]);
  },

  cancelTimedEvent: (key: string) => {
    AnalyticsHelper._countly.q.push(['cancel_event', key]);
  },

  endTimedEvent: (key: string, event?: EventObject) => {
    AnalyticsHelper._countly.q.push([
      'end_event',
      {
        key,
        ...event,
      },
    ]);
  },

  trackSessions: () => {
    AnalyticsHelper._countly.q.push(['track_sessions']);
  },

  trackPageView: (pageName?: string, ignorePages?: string[], data?: Segments) => {
    AnalyticsHelper._countly.q.push(['track_pageview', pageName, ignorePages, data]);
  },

  trackClicks: () => {
    AnalyticsHelper._countly.q.push(['track_clicks']);
  },

  trackScrolls: () => {
    AnalyticsHelper._countly.q.push(['track_scrolls']);
  },

  setUserProperty: (key: string, value: string) => {
    AnalyticsHelper._countly.q.push(['userData.set', key, value]);
    AnalyticsHelper._countly.q.push(['userData.save']);
  },

  trackEvent: (eventName: string, data?: Segments) => {
    AnalyticsHelper._countly.q.push([
      'add_event',
      {
        key: eventName,
        segmentation: data,
      },
    ]);
  },
};

interface IAnalyticsHelper {
  _countly: Countly;
  _initialized: boolean;

  initCountly: () => void;

  trackErrors: (data?: Segments) => void;

  logError: (error: Error, data?: Segments) => void;

  recordError: (error: Error, nonFatal: boolean, data?: Segments) => void;

  addLog: (log: string) => void;

  addEvent: (key: string, event?: EventObject) => void;

  startTimedEvent: (key: string) => void;

  cancelTimedEvent: (key: string) => void;

  endTimedEvent: (key: string, event?: EventObject) => void;

  trackSessions: () => void;

  trackPageView: (pageName?: string, ignorePages?: string[], data?: Segments) => void;

  trackClicks: () => void;

  trackScrolls: () => void;

  setUserProperty: (key: string, value: string) => void;

  trackEvent: (eventName: string, data?: Segments) => void;
}

export const useAnalytics = () => {
  const [analyticsHelper] = useState<IAnalyticsHelper>(AnalyticsHelper);

  useEffect(() => {
    if (!analyticsHelper._initialized) {
      analyticsHelper.initCountly();
    }
  }, [analyticsHelper]);

  return analyticsHelper;
};

export const useTrackPageViews = () => {
  const analytics = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [analytics, location.pathname]);

  return null;
};
